import { useContext, useState } from 'react';
import {
  useProcessCard,
  useSubmitLegal,
  useGetPaymentMethod,
} from '@ltvco/refresh-lib/payments';
import { PaymentMethods } from '@ltvco/refresh-lib/v1';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { SUBSCRIPTION_PLAN_UNIQUE_NAME } from '../../../constants';

interface UsePaymentFlowReturn {
  processingPayment: boolean;
  processPayment: (
    formData: Record<string, string>,
    token: string,
    maskedNumber: string,
    createCardQuery: { mutate: (data: URLSearchParams, options: any) => void },
    callbacks?: {
      onProcessingStart?: () => void;
      onSuccess?: () => void;
      onError?: (errors?: string[]) => void;
    }
  ) => Promise<void>;
}

export const usePaymentFlow = (): UsePaymentFlowReturn => {
  const [processingPayment, setProcessingPayment] = useState<boolean>(false);
  const { logError } = useContext(AppConfig);
  const processCardQuery = useProcessCard();
  const updateLegalQuery = useSubmitLegal();
  const { refetch: refetchPaymentMethods } = useGetPaymentMethod();

  const processPayment = async (
    formData: Record<string, string>,
    token: string,
    maskedNumber: string,
    createCardQuery: { mutate: (data: URLSearchParams, options: any) => void },
    callbacks?: {
      onProcessingStart?: () => void;
      onSuccess?: () => void;
      onError?: (errors?: string[]) => void;
    }
  ) => {
    // Convert form data to the format expected by the API
    const creditCardData = Object.assign(
      {},
      ...Object.entries(formData).map(([key, value]) => ({
        [`credit_card[${key}]`]: value,
      }))
    );
    creditCardData['credit_card[card_number]'] = maskedNumber;
    creditCardData['credit_card[tokenex_token]'] = token;
    creditCardData['credit_card[expiration_year]'] = formatExpirationYear(
      creditCardData['credit_card[expiration_year]']
    );

    // Call the createCardQuery mutation
    createCardQuery.mutate(new URLSearchParams(creditCardData), {
      onSuccess: async () => {
        // Step 1: Card created successfully, now refetch payment methods
        const { data } = await refetchPaymentMethods();

        // Find the newly created active payment method
        const activeMethod = (data as any)?.payment_methods?.find(
          (pm: PaymentMethods) => pm.active
        );

        if (activeMethod) {
          // Step 2: Accept TOS and process payment
          setProcessingPayment(true);
          callbacks?.onProcessingStart?.();
          updateLegalQuery.mutate(undefined, {
            onSuccess: () => {
              // Step 3: TOS accepted, now process the payment
              processCardQuery.mutate(
                {
                  cardId: activeMethod.id,
                  plan: {
                    unique_key: SUBSCRIPTION_PLAN_UNIQUE_NAME,
                  },
                  source_of_payment: 'subscribe_page',
                },
                {
                  onSuccess: () => {
                    // Step 4: Payment processed successfully
                    setProcessingPayment(false);
                    callbacks?.onSuccess?.();
                  },
                  onError: (error: Error) => {
                    setProcessingPayment(false);
                    logError('Payment processing error', error);
                    callbacks?.onError?.([
                      'There was an error processing your payment.',
                    ]);
                  },
                }
              );
            },
            onError: (error) => {
              setProcessingPayment(false);
              logError('TOS update error', error as Error);
              callbacks?.onError?.([
                'There was an error accepting the Terms of Service.',
              ]);
            },
          });
        } else {
          // Handle error - card created but couldn't find active payment method
          callbacks?.onError?.([
            'Payment method created but could not be activated.',
          ]);
        }
      },
      onError: (error: Error) => {
        logError('Card creation error', error);
        callbacks?.onError?.(['There was an error adding your card.']);
      },
    });
  };

  return {
    processingPayment,
    processPayment,
  };
};

const formatExpirationYear = (year: string): string => {
  if (year.length === 2) {
    return `20${year}`;
  }
  return year;
};
